import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Input, showSnackbar } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";

import { Language } from "imports/Utils";

import { userPushSettingSchema, UserPushSettingSchemaInput, UserPushSettingSchemaOutput } from "view/users/utils/user.schema";

import { ChildProps } from "types/commons";

import { GetAccountByIdQuery } from "__generated__/view/users/graphql/user.queries.generated";
import { UpdateJsonPushSettingsDocument } from "__generated__/view/users/graphql/user.mutations.generated";

export type AccountFromQuery = ChildProps<GetAccountByIdQuery, "getAccountById">

const UserPushSettingsDialog = ({ open, onClose, user, refetch }: {
	open: boolean;
	onClose: () => void;
	user: AccountFromQuery;
	refetch: () => void;
}) => {

	const { handleSubmit, control, reset } = useForm<UserPushSettingSchemaInput, unknown, UserPushSettingSchemaOutput>({
		resolver: zodResolver(userPushSettingSchema),
		defaultValues: {
			jsonUrl: "",
			jsonUsername: "",
			jsonPassword: "",
			confirmPassword: "",
			customHeaderKey: "",
			customHeaderValue: "",
		},
	});

	const [ updateJsonPushSettings, { loading } ] = useMutation(UpdateJsonPushSettingsDocument, {
		onCompleted: () => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
			handleOnClose();
			refetch();
		},
	});

	const handleOnClose = () => {
		onClose();
		reset();
	};

	const save = (data: UserPushSettingSchemaOutput) => {

		if (data.jsonPassword != data.confirmPassword) {
			showSnackbar(intl.formatMessage({ id: "PASSWORDS_DONT_MATCH" }), { variant: "error" });
			return;
		}

		updateJsonPushSettings({
			variables: {
				accountId: user.id,
				jsonPushSettings: {
					jsonUrl: data.jsonUrl,
					jsonUsername: data.jsonUsername,
					jsonPassword: data.jsonPassword,
					customHeaderKey: data.customHeaderKey,
					customHeaderValue: data.customHeaderValue,
				},
			},
		});
	};

	useEffect(() => {
		if (open && user.jsonPushSettings) {
			reset({
				jsonUrl: user.jsonPushSettings.jsonUrl ?? "",
				jsonUsername: user.jsonPushSettings.jsonUsername ?? "",
				jsonPassword: user.jsonPushSettings.jsonPassword ?? "",
				confirmPassword: "",
				customHeaderKey: user.jsonPushSettings.customHeaderKey ?? "",
				customHeaderValue: user.jsonPushSettings.customHeaderValue ?? "",
			});
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "USER_JSON_PUSH_SETTINGS" })}
			form='userPushSettingsForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="userPushSettingsForm">
				<FormGroup label={Language("USER_JSON_URL")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="jsonUrl"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>

				<FormGroup label={Language("USER_JSON_USERNAME")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="jsonUsername"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>

				<FormGroup label={Language("USER_JSON_PASSWORD")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="jsonPassword"
						control={control}
						render={({ field }) => <Input {...field} type="password" />} />
				</FormGroup>

				<FormGroup label={Language("CONFIRM_PASSWORD")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="confirmPassword"
						control={control}
						render={({ field }) => <Input {...field} type="password" />} />
				</FormGroup>

				<FormGroup label={Language("USER_CUSTOM_HEADER_KEY")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="customHeaderKey"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>

				<FormGroup label={Language("USER_CUSTOM_HEADER_VALUE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="customHeaderValue"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default UserPushSettingsDialog;
